import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useClickOutside from '../../hooks/useClickOutside';

/**
 * Floating input for entering text annotation content
 */
const TextAnnotationInput = ({
  position,
  onSubmit,
  onCancel,
}) => {
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close on outside click (with delay to avoid immediate close from the click that opened this)
  useClickOutside(containerRef, onCancel, { delay: 100 });

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (text.trim()) {
      onSubmit?.(text.trim());
    } else {
      onCancel?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    } else if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: position?.y ?? 100,
        left: position?.x ?? 100,
        zIndex: 10000,
        backgroundColor: 'var(--dwlf-elevated)',
        border: '1px solid #3b82f6',
        borderRadius: 6,
        padding: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
      }}
    >
      <form onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter text..."
          rows={3}
          style={{
            width: 200,
            padding: '8px 10px',
            backgroundColor: 'var(--dwlf-elevated)',
            border: '1px solid var(--dwlf-ink-20)',
            borderRadius: 4,
            color: 'var(--dwlf-text)',
            fontSize: 13,
            outline: 'none',
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
          maxLength={500}
        />
        <div style={{ 
          display: 'flex', 
          gap: 6, 
          marginTop: 8,
          justifyContent: 'flex-end'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '5px 10px',
              backgroundColor: 'var(--dwlf-ink-12)',
              border: '1px solid var(--dwlf-ink-20)',
              borderRadius: 4,
              color: 'var(--dwlf-ink-50)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            style={{
              padding: '5px 10px',
              backgroundColor: '#3b82f6',
              border: 'none',
              borderRadius: 4,
              color: 'var(--dwlf-text)',
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            Add
          </button>
        </div>
      </form>
      <div style={{ 
        fontSize: 10, 
        color: 'var(--dwlf-ink-40)', 
        marginTop: 6,
        textAlign: 'center'
      }}>
        Enter to confirm • Shift+Enter for new line • ESC to cancel
      </div>
    </div>
  );
};

TextAnnotationInput.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

export default TextAnnotationInput;
